/**
 * ReactionBurst - animated floating emoji reactions
 * Displays emoji bursts that float up from the bottom
 */

import { useState, useEffect, useRef, memo } from "react";
import type { ReactionEmoji, ReactionBurstItem } from "@/lib/websocket";
import styles from "./ReactionBurst.module.css";

type AnimationType = 'Float' | 'FloatLeft' | 'FloatRight' | 'FloatCenter';

interface FloatingReaction {
  id: string;
  emoji: ReactionEmoji;
  x: number; // percentage from left
  delay: number; // animation delay in ms
  scale: number; // random scale
  animationType: AnimationType; // which animation to use (assigned at creation, never changes)
}

interface ReactionBurstProps {
  /** Array of reaction bursts to display */
  reactions: ReactionBurstItem[];
  /** Unique trigger key to spawn new reactions */
  trigger?: number;
  /** Position: "left" | "right" | "full" | "sides" */
  position?: "left" | "right" | "full" | "sides";
  /** Size variant */
  size?: "small" | "medium" | "large";
}

let reactionIdCounter = 0;

export const ReactionBurst = memo(function ReactionBurst({
  reactions,
  trigger,
  position = "right",
  size = "medium",
}: ReactionBurstProps) {
  const [floatingReactions, setFloatingReactions] = useState<FloatingReaction[]>([]);
  const prevTriggerRef = useRef(trigger);

  // Generate floating reactions when trigger changes
  useEffect(() => {
    if (trigger === prevTriggerRef.current) return;
    prevTriggerRef.current = trigger;

    if (reactions.length === 0) return;

    const newReactions: FloatingReaction[] = [];
    
    // Helper to get random x position based on position mode
    const getXPosition = (): number => {
      if (position === "left") {
        return 5 + Math.random() * 30; // 5-35%
      } else if (position === "right") {
        return 65 + Math.random() * 20; // 65-85% (leaves space for FAB button at right edge)
      } else if (position === "sides") {
        // Randomly choose left or right side
        const isLeft = Math.random() > 0.5;
        if (isLeft) {
          return 3 + Math.random() * 17; // 3-20%
        } else {
          return 80 + Math.random() * 17; // 80-97%
        }
      }
      // "full" - entire width
      return 5 + Math.random() * 90; // 5-95%
    };

    // Animation types to randomly assign (stable once assigned)
    const animationTypes: AnimationType[] = ['FloatLeft', 'FloatRight', 'FloatCenter'];
    
    // Create floating reactions for each burst item
    for (const item of reactions) {
      for (let i = 0; i < item.count; i++) {
        newReactions.push({
          id: `reaction-${++reactionIdCounter}`,
          emoji: item.emoji,
          x: getXPosition(),
          delay: Math.random() * 400,
          scale: 0.85 + Math.random() * 0.4,
          animationType: animationTypes[Math.floor(Math.random() * animationTypes.length)],
        });
      }
    }

    setFloatingReactions((prev) => [...prev, ...newReactions]);

    // Clean up old reactions after animation completes (5.5-6s animation + buffer)
    setTimeout(() => {
      setFloatingReactions((prev) =>
        prev.filter((r) => !newReactions.find((nr) => nr.id === r.id))
      );
    }, 6500);
  }, [trigger, reactions, position]);

  // Cleanup all on unmount
  useEffect(() => {
    return () => {
      setFloatingReactions([]);
    };
  }, []);

  const sizeClass = size === "large" ? styles.large : size === "small" ? styles.small : "";

  return (
    <div className={`${styles.container} ${sizeClass}`}>
      {floatingReactions.map((reaction) => (
        <div
          key={reaction.id}
          className={styles.reaction}
          style={{
            left: `${reaction.x}%`,
            animationDelay: `${reaction.delay}ms`,
            '--scale': reaction.scale,
            animationName: styles[`reaction${reaction.animationType}`],
            // FloatCenter uses longer duration
            animationDuration: reaction.animationType === 'FloatCenter' ? '6s' : undefined,
          } as React.CSSProperties}
        >
          {reaction.emoji}
        </div>
      ))}
    </div>
  );
});

export default ReactionBurst;
